/**
 * ADIOSOFT ISOLATED COLOR SYSTEM & THEME TOKENS
 * 
 * Strict architectural isolation between:
 * 1. Admin App (.admin-shell)
 * 2. Customer App (.customer-shell)
 * 3. Editor UI (.editor-shell)
 * 4. Website Renderer (.website-renderer)
 * 
 * Rules:
 * - NO global colors in :root
 * - NO body background or color mutation
 * - NO leakage across contexts
 * - Centralized semantic tokens with dedicated defaults
 */

export interface AdminThemeTokens {
  '--admin-bg': string;
  '--admin-surface': string;
  '--admin-surface-secondary': string;
  '--admin-surface-elevated': string;
  '--admin-surface-hover': string;
  '--admin-text': string;
  '--admin-text-secondary': string;
  '--admin-text-muted': string;
  '--admin-heading': string;
  '--admin-border': string;
  '--admin-input-bg': string;
  '--admin-input-text': string;
  '--admin-input-placeholder': string;
  '--admin-primary': string;
  '--admin-primary-text': string;
  '--admin-secondary': string;
  '--admin-secondary-text': string;
  '--admin-focus': string;
  '--admin-danger': string;
  '--admin-danger-text': string;
  '--admin-success': string;
  '--admin-success-text': string;
  '--admin-warning': string;
  '--admin-warning-text': string;
}

export interface CustomerThemeTokens {
  '--customer-bg': string;
  '--customer-surface': string;
  '--customer-surface-secondary': string;
  '--customer-surface-elevated': string;
  '--customer-text': string;
  '--customer-text-secondary': string;
  '--customer-text-muted': string;
  '--customer-heading': string;
  '--customer-border': string;
  '--customer-input-bg': string;
  '--customer-input-text': string;
  '--customer-input-placeholder': string;
  '--customer-primary': string;
  '--customer-primary-text': string;
  '--customer-focus': string;
  '--customer-danger': string;
  '--customer-success': string;
  '--customer-warning': string;
}

export interface EditorThemeTokens {
  '--editor-bg': string;
  '--editor-surface': string;
  '--editor-surface-secondary': string;
  '--editor-surface-elevated': string;
  '--editor-text': string;
  '--editor-text-secondary': string;
  '--editor-text-muted': string;
  '--editor-heading': string;
  '--editor-border': string;
  '--editor-input-bg': string;
  '--editor-input-text': string;
  '--editor-input-placeholder': string;
  '--editor-primary': string;
  '--editor-primary-text': string;
  '--editor-focus': string;
}

export interface WebsiteThemeTokens {
  '--site-background': string;
  '--site-surface': string;
  '--site-surface-secondary': string;
  '--site-text': string;
  '--site-text-secondary': string;
  '--site-text-muted': string;
  '--site-heading': string;
  '--site-link': string;
  '--site-primary': string;
  '--site-primary-text': string;
  '--site-secondary': string;
  '--site-secondary-text': string;
  '--site-accent': string;
  '--site-border': string;
  '--site-surface-border': string;
  '--site-font': string;
  '--site-heading-font': string;
  '--site-input-background': string;
  '--site-input-text': string;
  '--site-input-placeholder': string;
  '--site-input-border': string;
  '--site-button-background': string;
  '--site-button-text': string;
  '--site-success': string;
  '--site-warning': string;
  '--site-danger': string;
}

/**
 * 1. ADMIN DEFAULT THEME (Dark, high-contrast, premium ops center)
 */
export const ADMIN_DEFAULT_THEME: AdminThemeTokens = {
  '--admin-bg': '#030712',
  '--admin-surface': '#0f172a',
  '--admin-surface-secondary': '#1e293b',
  '--admin-surface-elevated': '#1e293b',
  '--admin-surface-hover': '#24334a',
  '--admin-text': '#f8fafc',
  '--admin-text-secondary': '#cbd5e1',
  '--admin-text-muted': '#94a3b8',
  '--admin-heading': '#ffffff',
  '--admin-border': '#1e293b',
  '--admin-input-bg': '#0b1324',
  '--admin-input-text': '#f8fafc',
  '--admin-input-placeholder': '#64748b',
  '--admin-primary': '#e11d48', // High-contrast crimson / rose
  '--admin-primary-text': '#ffffff',
  '--admin-secondary': '#334155',
  '--admin-secondary-text': '#f8fafc',
  '--admin-focus': '#f43f5e',
  '--admin-danger': '#ef4444',
  '--admin-danger-text': '#ffffff',
  '--admin-success': '#10b981',
  '--admin-success-text': '#022c22',
  '--admin-warning': '#f59e0b',
  '--admin-warning-text': '#451a03',
};

/**
 * 2. CUSTOMER DEFAULT THEME (Clean, modern SaaS dashboard)
 */
export const CUSTOMER_DEFAULT_THEME: CustomerThemeTokens = {
  '--customer-bg': '#f8fafc',
  '--customer-surface': '#ffffff',
  '--customer-surface-secondary': '#f1f5f9',
  '--customer-surface-elevated': '#f1f5f9',
  '--customer-text': '#0f172a',
  '--customer-text-secondary': '#334155',
  '--customer-text-muted': '#64748b',
  '--customer-heading': '#0f172a',
  '--customer-border': '#e2e8f0',
  '--customer-input-bg': '#ffffff',
  '--customer-input-text': '#0f172a',
  '--customer-input-placeholder': '#94a3b8',
  '--customer-primary': '#2563eb', // Royal Blue
  '--customer-primary-text': '#ffffff',
  '--customer-focus': '#3b82f6',
  '--customer-danger': '#ef4444',
  '--customer-success': '#10b981',
  '--customer-warning': '#f59e0b',
};

/**
 * 3. EDITOR DEFAULT THEME (Focused studio workstation)
 */
export const EDITOR_DEFAULT_THEME: EditorThemeTokens = {
  '--editor-bg': '#020617',
  '--editor-surface': '#0f172a',
  '--editor-surface-secondary': '#1e293b',
  '--editor-surface-elevated': '#1e293b',
  '--editor-text': '#f8fafc',
  '--editor-text-secondary': '#cbd5e1',
  '--editor-text-muted': '#64748b',
  '--editor-heading': '#ffffff',
  '--editor-border': '#1e293b',
  '--editor-input-bg': '#0b1324',
  '--editor-input-text': '#f8fafc',
  '--editor-input-placeholder': '#64748b',
  '--editor-primary': '#3b82f6',
  '--editor-primary-text': '#ffffff',
  '--editor-focus': '#60a5fa',
};

/**
 * 4. WEBSITE DEFAULT THEME (Neutral canvas with high contrast)
 */
export const WEBSITE_DEFAULT_THEME: WebsiteThemeTokens = {
  '--site-background': '#ffffff',
  '--site-surface': '#f8fafc',
  '--site-surface-secondary': '#f1f5f9',
  '--site-text': '#0f172a',
  '--site-text-secondary': '#475569',
  '--site-text-muted': '#64748b',
  '--site-heading': '#0f172a',
  '--site-link': '#2563eb',
  '--site-primary': '#2563eb',
  '--site-primary-text': '#ffffff',
  '--site-secondary': '#0f172a',
  '--site-secondary-text': '#ffffff',
  '--site-accent': '#38bdf8',
  '--site-border': '#e2e8f0',
  '--site-surface-border': 'rgba(0, 0, 0, 0.08)',
  '--site-font': 'Inter, system-ui, sans-serif',
  '--site-heading-font': 'Plus Jakarta Sans, sans-serif',
  '--site-input-background': '#ffffff',
  '--site-input-text': '#0f172a',
  '--site-input-placeholder': '#94a3b8',
  '--site-input-border': '#cbd5e1',
  '--site-button-background': '#2563eb',
  '--site-button-text': '#ffffff',
  '--site-success': '#10b981',
  '--site-warning': '#f59e0b',
  '--site-danger': '#ef4444',
};

/**
 * LocalStorage Namespace Keys
 */
export const STORAGE_KEYS = {
  ADMIN_THEME: 'adiosoft.admin.theme',
  CUSTOMER_THEME: 'adiosoft.customer.theme',
  EDITOR_THEME: 'adiosoft.editor.theme',
  WEBSITE_THEME: (websiteId: string) => `adiosoft.website.theme:${websiteId}`,
} as const;

/**
 * Convert any theme token object into CSS custom property style object for inline assignment on container element
 */
export function tokensToCssVars(tokens: Record<string, string>): React.CSSProperties {
  return tokens as unknown as React.CSSProperties;
}
